import { Decimal } from '../../datastructures';
import { Nullable } from '../../interfaces';

export const BRIDGE_NETWORKS_ENDPOINT = 'https://api.layerswap.io/api/v2/networks';
export const BRIDGE_DESTINATION_NETWORKS_ENDPOINT = 'https://api.layerswap.io/api/v2/destinations';
export const BRIDGE_LIMIT_ENDPOINT = 'https://api.layerswap.io/api/v2/limits';
export const BRIDGE_QUOTE_ENDPOINT = 'https://api.layerswap.io/api/v2/quote';
export const BRIDGE_SWAP_ENDPOINT = 'https://api.layerswap.io/api/v2/swaps';
export const BRIDGE_SOURCES_ENDPOINT = 'https://api.layerswap.io/api/v2/sources';

export const API_HEADERS = {
  'X-LS-APIKEY': import.meta.env.VITE_APP_LAYERSWAP_API_KEY,
  accept: 'application/json',
};
export const SUPPORTED_NETWORK_TYPES = ['starknet', 'evm'];
export const HIDDEN_NETWORKS = [
  // Layerswap API calls are failing for these networks, so we hide them for now
  'EVMOS_MAINNET',
  'FUSE_MAINNET',
  'GNOSIS_MAINNET',
  'HECO_MAINNET',
  'KCC_MAINNET',
  'LIGHTLINK_MAINNET',
  'REDSTONE_MAINNET',
  'ROLLUX_MAINNET',

  // Networks not supported by wagmi
  'MINT_MAINNET',
  'NAHMII_MAINNET',
  'PARADEX_MAINNET',
];
export const POPULAR_NETWORKS: string[] = [
  'ARBITRUM_MAINNET',
  'BASE_MAINNET',
  'ETHEREUM_MAINNET',
  'OPTIMISM_MAINNET',
  'STARKNET_MAINNET',
  'ZKSYNCERA_MAINNET',
];

export interface BridgeRawToken {
  symbol: string;
  logo: string;
  contract: string;
  decimals: number;
  price_in_usd: number;
  precision: number;
  listing_date: string;
}

export interface BridgeRawNetwork {
  tokens: BridgeRawToken[];
  name: string;
  display_name: string;
  logo: string;
  chain_id: string;
  node_url: string;
  type: string;
  transaction_explorer_template: string;
  account_explorer_template: string;
  token: BridgeRawToken;
  metadata: {
    listing_date: string;
    evm_oracle_contract?: Nullable<string>;
    evm_multicall_contract?: Nullable<string>;
  };
}

export interface BridgeRawLimit {
  min_amount_in_usd: number;
  min_amount: number;
  max_amount_in_usd: number;
  max_amount: number;
}

export interface BridgeRawQuote {
  total_fee: number;
  total_fee_in_usd: number;
  receive_amount: number;
  min_receive_amount: number;
  blockchain_fee: number;
  service_fee: number;
  avg_completion_time: string;
  refuel_in_source: Nullable<number>;
  slippage: number;
}

export interface BridgeRawReward {
  token: BridgeRawToken;
  network: BridgeRawNetwork;
  amount: number;
  amount_in_usd: number;
}

export interface BridgeRawTransaction {
  from: Nullable<string>;
  to: Nullable<string>;
  timestamp: string;
  transaction_hash: Nullable<string>;
  amount: number;
  status: string;
  token: BridgeRawToken;
  network: BridgeRawNetwork;
}

export interface BridgeRawSwap {
  id: string;
  created_date: string;
  source_network: BridgeRawNetwork;
  source_token: BridgeRawToken;
  destination_network: BridgeRawNetwork;
  destination_token: BridgeRawToken;
  requested_amount: number;
  destination_address: string;
  status: 'user_transfer_pending' | 'ls_transfer_pending' | 'completed';
  fail_reason: Nullable<string>;
  use_deposit_address: boolean;
  transactions: BridgeRawTransaction[];
}

export interface BridgeToken {
  symbol: string;
  logo: string;
  contract: string;
  decimals: number;
  price: Decimal;
  precision: number;
  listingDate: Date;
}

export interface BridgeNetwork {
  tokens: BridgeToken[];
  name: string;
  displayName: string;
  logo: string;
  chainId: string;
  nodeUrl: string;
  type: string;
  transactionExplorerTemplate: string;
  accountExplorerTemplate: string;
  token: Nullable<BridgeToken>;
  metadata: { listingDate: Date; evmOracleContract?: Nullable<string>; evmMulticallContract?: Nullable<string> };
}

export interface BridgeLimit {
  minAmountInUsd: Decimal;
  minAmount: Decimal;
  maxAmountInUsd: Decimal;
  maxAmount: Decimal;
}

export interface BridgeQuote {
  totalFee: Decimal;
  totalFeeInUsd: Decimal;
  receiveAmount: Decimal;
  minReceiveAmount: Decimal;
  blockchainFee: Decimal;
  serviceFee: Decimal;
  avgCompletionTime: string;
  refuelInSource: Nullable<Decimal>;
  slippage: Decimal;
}

export interface BridgeReward {
  token: BridgeToken;
  network: BridgeNetwork;
  amount: Decimal;
  amountInUsd: Decimal;
}

export interface BridgeTransaction {
  from: Nullable<string>;
  to: Nullable<string>;
  date: Date;
  hash: Nullable<string>;
  amount: Decimal;
  status: string;
  token: BridgeToken;
  network: BridgeNetwork;
}

export const toBridgeError = (error: { code: string; message: string }): Error =>
  new Error(`${error.code}: ${error.message}`);

export const toBridgeToken = (token: BridgeRawToken): BridgeToken => ({
  symbol: token.symbol,
  logo: token.logo,
  contract: token.contract,
  decimals: token.decimals,
  price: Decimal.parse(token.price_in_usd, 0),
  precision: token.precision,
  listingDate: new Date(token.listing_date),
});

export const toBridgeNetwork = (network: BridgeRawNetwork): BridgeNetwork => ({
  tokens: network.tokens ? network.tokens.map(toBridgeToken) : network.tokens,
  name: network.name,
  displayName: network.display_name,
  logo: network.logo,
  chainId: network.chain_id,
  nodeUrl: network.node_url,
  type: network.type,
  transactionExplorerTemplate: network.transaction_explorer_template,
  accountExplorerTemplate: network.account_explorer_template,
  token: network.token ? toBridgeToken(network.token) : null,
  metadata: {
    listingDate: new Date(network.metadata.listing_date),
    evmOracleContract: network.metadata.evm_oracle_contract,
    evmMulticallContract: network.metadata.evm_multicall_contract,
  },
});

export const toBridgeLimit = (limit: BridgeRawLimit): BridgeLimit => ({
  minAmountInUsd: Decimal.parse(limit.min_amount_in_usd, 0),
  minAmount: Decimal.parse(limit.min_amount, 0),
  maxAmountInUsd: Decimal.parse(limit.max_amount_in_usd, 0),
  maxAmount: Decimal.parse(limit.max_amount, 0),
});

export const toBridgeQuote = (quote: BridgeRawQuote): BridgeQuote => ({
  totalFee: Decimal.parse(quote.total_fee, 0),
  totalFeeInUsd: Decimal.parse(quote.total_fee_in_usd, 0),
  receiveAmount: Decimal.parse(quote.receive_amount, 0),
  minReceiveAmount: Decimal.parse(quote.min_receive_amount, 0),
  blockchainFee: Decimal.parse(quote.blockchain_fee, 0),
  serviceFee: Decimal.parse(quote.service_fee, 0),
  avgCompletionTime: quote.avg_completion_time,
  refuelInSource: quote.refuel_in_source ? Decimal.parse(quote.refuel_in_source, 0) : null,
  slippage: Decimal.parse(quote.slippage, 0),
});

export const toBridgeReward = (reward: BridgeRawReward): BridgeReward => ({
  token: toBridgeToken(reward.token),
  network: toBridgeNetwork(reward.network),
  amount: Decimal.parse(reward.amount, 0),
  amountInUsd: Decimal.parse(reward.amount_in_usd, 0),
});

export const toBridgeTransaction = (transaction: BridgeRawTransaction): BridgeTransaction => ({
  from: transaction.from,
  to: transaction.to,
  date: new Date(transaction.timestamp),
  hash: transaction.transaction_hash,
  amount: Decimal.parse(transaction.amount, 0),
  status: transaction.status,
  token: toBridgeToken(transaction.token),
  network: toBridgeNetwork(transaction.network),
});
